import type { QueryClient, QueryFunction } from "@tanstack/react-query";
import { createStore, useStore, type StoreApi } from "zustand";

import type { Schema } from "@bw/shared/types/generated";

import { getPlatformAdminRoster } from "@/shared/api/platformAdminAccess";

export const PLATFORM_ADMIN_ROSTER_QUERY_KEY = [
  "platform",
  "admin-access",
  "roster",
] as const;
export const PLATFORM_ADMIN_CANDIDATES_QUERY_KEY = [
  "platform",
  "admin-access",
  "candidates",
] as const;

export type PlatformAdminAccessRecovery = Readonly<
  | {
      email: string;
      kind: "invite" | "revoke";
      surface: "settings";
    }
  | {
      email: string;
      kind: "invite";
      surface: "user";
      userId: string;
    }
>;

export type PlatformAdminAccessResolution = Readonly<{
  outcome: "confirmed" | "diverged" | "identity-mismatch";
  recovery: PlatformAdminAccessRecovery;
  sequence: number;
}>;

export type PlatformAdminAccessWriteLease = Readonly<{
  generation: number;
  id: number;
}>;

interface PlatformAdminAccessWriteFence {
  generation: number;
  nextId: number;
  pending: PlatformAdminAccessWriteLease | null;
}

interface PlatformAdminAccessAuthorityState {
  recovery: PlatformAdminAccessRecovery | null;
  refreshing: boolean;
  resolution: PlatformAdminAccessResolution | null;
  resolutionSequence: number;
  writing: boolean;
  beginWriteOperation: () => PlatformAdminAccessWriteLease | null;
  clearResolution: (expected: PlatformAdminAccessResolution) => boolean;
  finishRefresh: (expected: PlatformAdminAccessRecovery) => void;
  finishWrite: (lease?: PlatformAdminAccessWriteLease) => void;
  isWriteOperationCurrent: (lease: PlatformAdminAccessWriteLease) => boolean;
  recordRecovery: (recovery: PlatformAdminAccessRecovery) => void;
  recordResolution: (
    recovery: PlatformAdminAccessRecovery,
    outcome: PlatformAdminAccessResolution["outcome"],
  ) => void;
  resolveRecovery: (
    expected: PlatformAdminAccessRecovery,
    outcome: PlatformAdminAccessResolution["outcome"],
  ) => boolean;
  reset: () => void;
  tryStartRefresh: (expected: PlatformAdminAccessRecovery) => boolean;
  tryStartWrite: () => boolean;
}

type PlatformAdminAccessAuthorityStore =
  StoreApi<PlatformAdminAccessAuthorityState>;

const storesByQueryClient = new WeakMap<
  QueryClient,
  PlatformAdminAccessAuthorityStore
>();
const fencesByQueryClient = new WeakMap<
  QueryClient,
  PlatformAdminAccessWriteFence
>();

function writeFenceFor(
  queryClient: QueryClient,
): PlatformAdminAccessWriteFence {
  const existing = fencesByQueryClient.get(queryClient);
  if (existing) return existing;
  const fence: PlatformAdminAccessWriteFence = {
    generation: 0,
    nextId: 0,
    pending: null,
  };
  fencesByQueryClient.set(queryClient, fence);
  return fence;
}

function nextResolution(
  state: PlatformAdminAccessAuthorityState,
  recovery: PlatformAdminAccessRecovery,
  outcome: PlatformAdminAccessResolution["outcome"],
): PlatformAdminAccessResolution {
  return Object.freeze({
    outcome,
    recovery,
    sequence: state.resolutionSequence + 1,
  });
}

function createPlatformAdminAccessAuthorityStore(
  queryClient: QueryClient,
  fence: PlatformAdminAccessWriteFence,
): PlatformAdminAccessAuthorityStore {
  return createStore<PlatformAdminAccessAuthorityState>((set) => ({
    recovery: null,
    refreshing: false,
    resolution: null,
    resolutionSequence: 0,
    writing: fence.pending !== null,
    beginWriteOperation: () => {
      let lease: PlatformAdminAccessWriteLease | null = null;
      set((state) => {
        if (!state.writing || fence.pending !== null) return state;
        lease = Object.freeze({
          generation: fence.generation,
          id: ++fence.nextId,
        });
        fence.pending = lease;
        return state;
      });
      return lease;
    },
    clearResolution: (expected) => {
      let cleared = false;
      set((state) => {
        if (state.resolution !== expected) return state;
        cleared = true;
        return { resolution: null };
      });
      return cleared;
    },
    finishRefresh: (expected) =>
      set((state) => {
        const refreshMatches =
          state.recovery === expected ||
          (state.resolution?.outcome === "diverged" &&
            state.resolution.recovery === expected);
        return refreshMatches && state.refreshing
          ? { refreshing: false }
          : state;
      }),
    finishWrite: (lease) => {
      if (lease && fence.pending === lease) {
        fence.pending = null;
      }
      const writing = fence.pending !== null;
      set({ writing });
      storesByQueryClient.get(queryClient)?.setState({ writing });
    },
    isWriteOperationCurrent: (lease) =>
      fence.pending === lease && lease.generation === fence.generation,
    recordRecovery: (recovery) =>
      set({ recovery, refreshing: false, resolution: null }),
    recordResolution: (recovery, outcome) =>
      set((state) => {
        const resolution = nextResolution(state, recovery, outcome);
        return {
          recovery: null,
          refreshing: false,
          resolution,
          resolutionSequence: resolution.sequence,
        };
      }),
    resolveRecovery: (expected, outcome) => {
      let resolved = false;
      set((state) => {
        if (state.recovery !== expected) return state;
        resolved = true;
        const resolution = nextResolution(state, expected, outcome);
        return {
          recovery: null,
          refreshing: false,
          resolution,
          resolutionSequence: resolution.sequence,
        };
      });
      return resolved;
    },
    reset: () =>
      set({
        recovery: null,
        refreshing: false,
        resolution: null,
        resolutionSequence: 0,
        writing: false,
      }),
    tryStartRefresh: (expected) => {
      let started = false;
      set((state) => {
        const refreshMatches =
          state.recovery === expected ||
          (state.resolution?.outcome === "diverged" &&
            state.resolution.recovery === expected);
        if (!refreshMatches || state.refreshing || state.writing) {
          return state;
        }
        started = true;
        return { refreshing: true };
      });
      return started;
    },
    tryStartWrite: () => {
      let started = false;
      set((state) => {
        if (state.recovery || state.refreshing || state.writing) return state;
        started = true;
        return { resolution: null, writing: true };
      });
      return started;
    },
  }));
}

export function platformAdminAccessAuthorityStoreFor(
  queryClient: QueryClient,
): PlatformAdminAccessAuthorityStore {
  const existing = storesByQueryClient.get(queryClient);
  if (existing) return existing;
  const store = createPlatformAdminAccessAuthorityStore(
    queryClient,
    writeFenceFor(queryClient),
  );
  storesByQueryClient.set(queryClient, store);
  return store;
}

export function usePlatformAdminAccessAuthority(queryClient: QueryClient) {
  return useStore(platformAdminAccessAuthorityStoreFor(queryClient));
}

export function resetPlatformAdminAccessAuthority(
  queryClient: QueryClient,
): void {
  writeFenceFor(queryClient).generation += 1;
  storesByQueryClient.get(queryClient)?.getState().reset();
  storesByQueryClient.delete(queryClient);
}

type Roster = Schema["PlatformAdminAccessRosterResponse"];
type Candidates = Schema["PlatformAdminAccessCandidatesResponse"];

export type AdminRosterReadEvidence = Readonly<{
  candidatesRefreshed: boolean;
  rosterRefreshed: boolean;
}>;

export type UserAdminReadEvidence = Readonly<{
  detailRefreshed: boolean;
  usersListRefreshed: boolean;
  usersListRequired: boolean;
}>;

export type AuthorityOutcome = "confirmed" | "diverged" | "uncertain";
export type AuthorityReadLease = () => boolean;

export function normalizePlatformAdminEmail(
  email: unknown,
): string | undefined {
  return typeof email === "string"
    ? email.trim().toLowerCase() || undefined
    : undefined;
}

async function readSucceeded(read: Promise<unknown>): Promise<boolean> {
  try {
    await read;
    return true;
  } catch {
    return false;
  }
}

function hasActiveQuery(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  exact = false,
): boolean {
  return (
    queryClient.getQueryCache().findAll({ exact, queryKey, type: "active" })
      .length > 0
  );
}

export async function refreshAdminRosterAuthority(
  queryClient: QueryClient,
  _mode: "reconcile" | "retry",
  operationIsCurrent: AuthorityReadLease = () => true,
): Promise<AdminRosterReadEvidence> {
  if (!operationIsCurrent()) {
    return { candidatesRefreshed: false, rosterRefreshed: false };
  }
  const candidatesRequired = hasActiveQuery(
    queryClient,
    PLATFORM_ADMIN_CANDIDATES_QUERY_KEY,
  );
  const usersListRequired = hasActiveQuery(queryClient, ["platform", "users"]);

  await Promise.all([
    queryClient.cancelQueries({
      exact: true,
      queryKey: PLATFORM_ADMIN_ROSTER_QUERY_KEY,
    }),
    candidatesRequired
      ? queryClient.cancelQueries({
          queryKey: PLATFORM_ADMIN_CANDIDATES_QUERY_KEY,
          type: "active",
        })
      : Promise.resolve(),
    usersListRequired
      ? queryClient.cancelQueries({
          queryKey: ["platform", "users"],
          type: "active",
        })
      : Promise.resolve(),
  ]);
  if (!operationIsCurrent()) {
    return { candidatesRefreshed: false, rosterRefreshed: false };
  }
  await Promise.all([
    queryClient.invalidateQueries({
      exact: true,
      queryKey: PLATFORM_ADMIN_ROSTER_QUERY_KEY,
      refetchType: "none",
    }),
    candidatesRequired
      ? queryClient.invalidateQueries({
          queryKey: PLATFORM_ADMIN_CANDIDATES_QUERY_KEY,
          refetchType: "none",
          type: "active",
        })
      : Promise.resolve(),
    queryClient.invalidateQueries({
      queryKey: ["platform", "users"],
      refetchType: "none",
    }),
  ]);

  if (!operationIsCurrent()) {
    return { candidatesRefreshed: false, rosterRefreshed: false };
  }

  // Start every required post-write read synchronously after the lease check.
  // An actor transition cannot interleave before these calls register with the
  // QueryClient; a later transition cancels them through QueryClient.clear().
  const rosterRead = queryClient.fetchQuery({
    queryKey: PLATFORM_ADMIN_ROSTER_QUERY_KEY,
    queryFn: () => getPlatformAdminRoster(80),
    retry: false,
    staleTime: 0,
  });
  const candidatesRead = candidatesRequired
    ? queryClient.refetchQueries(
        {
          queryKey: PLATFORM_ADMIN_CANDIDATES_QUERY_KEY,
          type: "active",
        },
        { throwOnError: true },
      )
    : Promise.resolve();
  const usersRead = usersListRequired
    ? queryClient.refetchQueries(
        { queryKey: ["platform", "users"], type: "active" },
        { throwOnError: true },
      )
    : Promise.resolve();
  const [rosterRefreshed, candidatesRefreshed] = await Promise.all([
    readSucceeded(rosterRead),
    candidatesRequired ? readSucceeded(candidatesRead) : Promise.resolve(false),
    usersListRequired ? readSucceeded(usersRead) : Promise.resolve(false),
  ]);
  return operationIsCurrent()
    ? { candidatesRefreshed, rosterRefreshed }
    : { candidatesRefreshed: false, rosterRefreshed: false };
}

export function adminRosterAuthorityOutcome(
  queryClient: QueryClient,
  recovery: PlatformAdminAccessRecovery,
  evidence: AdminRosterReadEvidence,
): AuthorityOutcome {
  const targetEmail = normalizePlatformAdminEmail(recovery.email);
  if (!targetEmail) return "diverged";
  const roster = queryClient.getQueryData<Roster>(
    PLATFORM_ADMIN_ROSTER_QUERY_KEY,
  );
  const rosterContainsTarget = roster?.items.some(
    (candidate) => normalizePlatformAdminEmail(candidate.email) === targetEmail,
  );
  if (recovery.kind === "revoke") {
    if (!evidence.rosterRefreshed || !roster) return "uncertain";
    return rosterContainsTarget ? "diverged" : "confirmed";
  }

  if (!evidence.rosterRefreshed || !roster) return "uncertain";
  const rosterConfirms = Boolean(
    roster.items.some(
      (candidate) =>
        normalizePlatformAdminEmail(candidate.email) === targetEmail &&
        candidate.access_state === "already_admin",
    ),
  );
  if (!rosterConfirms) return "diverged";
  const candidates = evidence.candidatesRefreshed
    ? queryClient.getQueriesData<Candidates>({
        queryKey: PLATFORM_ADMIN_CANDIDATES_QUERY_KEY,
        type: "active",
      })
    : [];
  for (const [, result] of candidates) {
    const matchingCandidate = result?.items.find(
      (candidate) =>
        normalizePlatformAdminEmail(candidate.email) === targetEmail,
    );
    if (
      matchingCandidate &&
      matchingCandidate.access_state !== "already_admin"
    ) {
      return "diverged";
    }
  }
  return "confirmed";
}

export async function refreshUserAdminAuthority(
  queryClient: QueryClient,
  userId: string,
  _mode: "reconcile" | "retry",
  operationIsCurrent: AuthorityReadLease = () => true,
): Promise<UserAdminReadEvidence> {
  const detailKey = ["platform", "user", userId] as const;
  const detailQuery = queryClient
    .getQueryCache()
    .find({ exact: true, queryKey: detailKey });
  const detailQueryFn = detailQuery?.options.queryFn;
  const detailRequired = typeof detailQueryFn === "function";
  const usersListRequired = hasActiveQuery(queryClient, ["platform", "users"]);
  if (!operationIsCurrent()) {
    return {
      detailRefreshed: false,
      usersListRefreshed: false,
      usersListRequired,
    };
  }
  await Promise.all([
    detailRequired
      ? queryClient.cancelQueries({ exact: true, queryKey: detailKey })
      : Promise.resolve(),
    usersListRequired
      ? queryClient.cancelQueries({
          queryKey: ["platform", "users"],
          type: "active",
        })
      : Promise.resolve(),
  ]);
  if (!operationIsCurrent()) {
    return {
      detailRefreshed: false,
      usersListRefreshed: false,
      usersListRequired,
    };
  }
  await Promise.all([
    detailRequired
      ? queryClient.invalidateQueries({
          exact: true,
          queryKey: detailKey,
          refetchType: "none",
        })
      : Promise.resolve(),
    queryClient.invalidateQueries({
      queryKey: ["platform", "users"],
      refetchType: "none",
    }),
  ]);

  if (!operationIsCurrent()) {
    return {
      detailRefreshed: false,
      usersListRefreshed: false,
      usersListRequired,
    };
  }

  const detailRead = detailRequired
    ? queryClient.fetchQuery({
        queryKey: detailKey,
        queryFn: detailQueryFn as QueryFunction<unknown, typeof detailKey>,
        retry: false,
        staleTime: 0,
      })
    : Promise.resolve();
  const usersRead = usersListRequired
    ? queryClient.refetchQueries(
        { queryKey: ["platform", "users"], type: "active" },
        { throwOnError: true },
      )
    : Promise.resolve();
  const [detailRefreshed, usersReadSucceeded] = await Promise.all([
    detailRequired ? readSucceeded(detailRead) : Promise.resolve(false),
    usersListRequired ? readSucceeded(usersRead) : Promise.resolve(true),
  ]);
  if (!operationIsCurrent()) {
    return {
      detailRefreshed: false,
      usersListRefreshed: false,
      usersListRequired,
    };
  }
  return {
    detailRefreshed,
    usersListRefreshed: usersListRequired ? usersReadSucceeded : true,
    usersListRequired,
  };
}

function hasPlatformAdminRole(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const roles = (value as Record<string, unknown>).roles;
  return Array.isArray(roles) && roles.includes("platform_admin");
}

function userIdentityMatches(
  value: unknown,
  expectedUid: string,
  expectedEmail: string,
): boolean {
  if (!value || typeof value !== "object") return false;
  const user = value as Record<string, unknown>;
  return (
    user.uid === expectedUid &&
    normalizePlatformAdminEmail(user.email) ===
      normalizePlatformAdminEmail(expectedEmail)
  );
}

export function userAdminAuthorityOutcome(
  queryClient: QueryClient,
  recovery: Extract<PlatformAdminAccessRecovery, { surface: "user" }>,
  evidence: UserAdminReadEvidence,
): AuthorityOutcome {
  if (!evidence.detailRefreshed) return "uncertain";
  const detail = queryClient.getQueryData([
    "platform",
    "user",
    recovery.userId,
  ]);
  if (
    !userIdentityMatches(detail, recovery.userId, recovery.email) ||
    !hasPlatformAdminRole(detail)
  ) {
    return "diverged";
  }
  if (evidence.usersListRequired && !evidence.usersListRefreshed) {
    return "uncertain";
  }
  if (evidence.usersListRequired) {
    const activeLists = queryClient.getQueriesData<unknown>({
      queryKey: ["platform", "users"],
      type: "active",
    });
    for (const [, value] of activeLists) {
      if (!value || typeof value !== "object") continue;
      const items = (value as Record<string, unknown>).items;
      if (!Array.isArray(items)) continue;
      for (const item of items) {
        if (!item || typeof item !== "object") continue;
        const candidate = item as Record<string, unknown>;
        const sameUid = candidate.uid === recovery.userId;
        const sameEmail =
          normalizePlatformAdminEmail(candidate.email) ===
          normalizePlatformAdminEmail(recovery.email);
        if (
          (sameUid || sameEmail) &&
          (!userIdentityMatches(item, recovery.userId, recovery.email) ||
            !hasPlatformAdminRole(item))
        ) {
          return "diverged";
        }
      }
    }
  }
  return "confirmed";
}
